import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { theme, card, emptyFrame, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { MarkdownDocument } from "../components/MarkdownDocument";
import { api } from "../api/client";
import { generatePolicyDocument, runDocumentJob, pollDocumentJob, isDocumentProgress, POLICY_GENERATION_WAIT_MS, type AnyJobProgress, type DocumentProgress, type JobStatus, type SetGenerationResult } from "../lib/generatePolicy";
import {
  ProvenancePanel, ProvenanceWarnings, InputsChangedChip, InputsChangedNote, TemplateInstructions,
  SectionRewriteForm, GenerateSetPanel, GenerationProgressView, type DocumentProvenance, type SetRunView,
} from "../components/PolicyGenerationExtras";
import { useFrameworks, FRAMEWORK_LABELS } from "../context/FrameworksContext";
import { FRAMEWORK_ORDER } from "../lib/frameworkCatalogue";
import { controlRefWithStandard } from "../lib/controlRef";
import { PortalLink } from "../components/PortalLink";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PolicyAddendaSection, AddendumScopeChips, type PolicyAddendum } from "../components/PolicyAddenda";
import { PolicyShortfallList } from "../components/PolicyShortfalls";
import { PolicyAdoptionPanel, AdoptionChip, type PolicyAdoptionRow } from "../components/PolicyAdoption";
import { PolicyOverlapPanel, PolicyOverlapHints, StandardSpreadChip, type PolicyOverlapReport } from "../components/PolicyOverlapPanel";
import { PolicyContradictionsPanel, type PolicyContradiction } from "../components/PolicyContradictionsPanel";
import { fmtDateOnly } from "../lib/dateOnly";
import { playbookPath, usePlaybooks } from "../lib/playbooks";
import { ShellLink } from "../components/ShellLink";

const GENERATING_KEY = "abacas_policy_generating";

/**
 * A generation in flight, kept across reloads: when it started and its job, so a page opened
 * later follows the job itself — and so learns when it failed, not only when it succeeded.
 * Entries written before the job id was kept are plain start times.
 */
interface GeneratingEntry { startedAt: number; jobId?: string }

function getGeneratingMap(): Record<string, GeneratingEntry> {
  try {
    const raw = JSON.parse(localStorage.getItem(GENERATING_KEY) ?? "{}") as Record<string, number | GeneratingEntry>;
    return Object.fromEntries(Object.entries(raw).map(([id, v]) => [id, typeof v === "number" ? { startedAt: v } : v]));
  } catch { return {}; }
}
function setGeneratingMap(map: Record<string, GeneratingEntry>) {
  try {
    Object.keys(map).length === 0 ? localStorage.removeItem(GENERATING_KEY) : localStorage.setItem(GENERATING_KEY, JSON.stringify(map));
  } catch { /* storage unavailable: the job still runs, a reload just cannot follow it */ }
}
function markGenerating(id: string, entry: GeneratingEntry) { const m = getGeneratingMap(); m[id] = entry; setGeneratingMap(m); }
function clearGenerating(id: string) { const m = getGeneratingMap(); delete m[id]; setGeneratingMap(m); }

// A set generation outlives the page: its job id is kept so a reload picks the progress up again.
const SET_JOB_KEY = "abacas_policy_set_job";
/** A set of up to 150 documents takes hours, not minutes. */
const SET_GENERATION_WAIT_MS = 8 * 60 * 60 * 1000;
/** The most templates one set generation accepts (POST /templates/generate-set). */
const SET_GENERATION_MAX = 150;
function getSetJob(): { jobId: string; startedAt: number } | null {
  try { return JSON.parse(localStorage.getItem(SET_JOB_KEY) ?? "null"); } catch { return null; }
}
function setSetJob(v: { jobId: string; startedAt: number } | null) {
  try {
    if (v) localStorage.setItem(SET_JOB_KEY, JSON.stringify(v));
    else localStorage.removeItem(SET_JOB_KEY);
  } catch { /* storage unavailable: progress is lost on reload, the job still runs */ }
}

interface PolicyTemplate {
  id: string;
  title: string;
  category: string;
  description: string;
  controls: { id: string; name: string; frameworkId: string | null }[];
  frameworkIds: string[];
  /** Cyber Essentials controls the policy evidences (no Control rows exist for CE), e.g. A4 Firewalls. */
  ceControls: { id: string; ref: string; name: string }[];
  // Data protection documents: the regulations whose obligations the template addresses.
  // Relevance is decided against the organisation's applicable regulations, not its frameworks.
  regulations: string[];
  kind: string;
  kindLabel: string;
  audience: string;
  /** The good-practice playbook a playbook template is written from (backend lib/playbookTemplates.ts). */
  playbookId?: string | null;
  /** "docx" for every policy and notice; "xlsx" for row-based registers (the RoPA). */
  fileFormat?: "docx" | "xlsx";
}

interface GeneratedDoc {
  id: string;
  versionNumber?: number;
  generatedAt: string;
  staleSince: string | null;
  staleReason?: string | null;
  /** What the version was written from; null for documents written before it was kept. */
  provenance?: DocumentProvenance | null;
}

interface DocState {
  status: "idle" | "checking" | "generating" | "loaded" | "error";
  content?: string;
  document?: GeneratedDoc;
  error?: string;
  generatingStartedAt?: number;
  /** The worker's own report: stage, pass, estimate (absent until the worker starts). */
  progress?: DocumentProgress;
  /** When the job was queued, while it is still waiting for the worker. */
  queuedAt?: number;
}

const STANDARD_FILTERS = [
  { key: "playbooks", label: "Playbooks", frameworkIds: [] as string[] },
  { key: "data_protection", label: "Data Protection", frameworkIds: ["legal"] },
  { key: "iso27001", label: "ISO 27001", frameworkIds: ["iso27001"] },
  { key: "ce",       label: "CE / CE+",  frameworkIds: ["cyber_essentials", "cyber_essentials_plus"] },
  { key: "iso9001",  label: "ISO 9001",  frameworkIds: ["iso9001"] },
  { key: "iso42001", label: "ISO 42001", frameworkIds: ["iso42001"] },
  { key: "soc2",     label: "SOC 2",     frameworkIds: ["soc2"] },
  { key: "pci_dss",  label: "PCI DSS",   frameworkIds: ["pci_dss", "pci_dss_saq_a", "pci_dss_saq_a_ep", "pci_dss_saq_b", "pci_dss_saq_b_ip", "pci_dss_saq_c", "pci_dss_saq_c_vt", "pci_dss_saq_d_merchant", "pci_dss_saq_d_sp", "pci_dss_saq_p2pe"] },
  { key: "iasme_ca", label: "IASME CA",  frameworkIds: ["iasme_ca_sole_trader", "iasme_ca_micro", "iasme_ca_small", "iasme_ca_medium_large"] },
  { key: "ncsc_caf", label: "NCSC CAF",  frameworkIds: ["ncsc_caf"] },
  { key: "iso45001", label: "ISO 45001", frameworkIds: ["iso45001"] },
  { key: "iso14001", label: "ISO 14001", frameworkIds: ["iso14001"] },
];

/**
 * The control chips a card shows: those of the standards the organisation is enrolled in (a Cyber
 * Essentials row for CE or CE+), so a Cyber Essentials organisation is not shown SOC 2 and PCI DSS
 * references. Every chip when none of them is enrolled.
 */
function chipControls(controls: PolicyTemplate["controls"], enrolled: Set<string>): PolicyTemplate["controls"] {
  const ce = enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus");
  const mine = controls.filter((c) => !c.frameworkId || enrolled.has(c.frameworkId) || (c.frameworkId === "cyber_essentials" && ce));
  return mine.length > 0 ? mine : controls;
}

function isDataProtectionTemplate(t: PolicyTemplate): boolean {
  return t.regulations.length > 0;
}

function isTemplateEnrolled(t: PolicyTemplate, enrolled: Set<string>, orgRegulations: string[]): boolean {
  // Data protection documents apply when one of the laws they address applies to the
  // organisation (Operating Locations, or gdpr / uk_gdpr enrolment — resolved server-side).
  if (isDataProtectionTemplate(t)) return t.regulations.some((r) => orgRegulations.includes(r));
  return t.frameworkIds.some((fwId) => {
    if (enrolled.has(fwId)) return true;
    // pci_dss (full standard) templates are also relevant for any SAQ variant
    if (fwId === "pci_dss") return [...enrolled].some((e) => e.startsWith("pci_dss"));
    return false;
  });
}

// Short chip labels for the regulations a data protection document covers.
const REGULATION_SHORT: Record<string, string> = {
  "EU GDPR": "EU GDPR",
  "UK GDPR": "UK GDPR",
  "Data Protection Act 2018": "DPA 2018",
  "Privacy and Electronic Communications Regulations 2003 (PECR)": "PECR",
  "Jersey Data Protection Law 2018": "Jersey DPJL 2018",
  "Guernsey Data Protection Law 2017": "Guernsey DPL 2017",
  "Isle of Man Data Protection Act 2018": "IoM DPA 2018",
  "Data Protection Acts 1988–2018": "Irish DPA",
  "BDSG (Federal Data Protection Act)": "BDSG",
  "Loi Informatique et Libertés": "Loi I&L",
  "Austrian Data Protection Act (DSG)": "DSG (AT)",
  "Personal Data Protection Code": "Codice Privacy (IT)",
  "Tietosuojalaki 1050/2018": "Tietosuojalaki (FI)",
  "Dataskyddslag 2018:218": "Dataskyddslag (SE)",
  "Databeskyttelsesloven": "Databeskyttelsesloven (DK)",
  "Personal Data Act (Personopplysningsloven)": "Personopplysningsloven (NO)",
  "nDSG (revised Federal Act on Data Protection)": "nDSG (CH)",
  "CCPA / CPRA (California)": "CCPA / CPRA",
  "HIPAA (healthcare)": "HIPAA",
  "Quebec Law 25 (Act 25)": "Quebec Law 25",
  "LGPD (Lei Geral de Proteção de Dados)": "LGPD",
  "Federal Law on Protection of Personal Data (LFPDPPP)": "LFPDPPP (MX)",
  "Protection of Personal Information Act (POPIA)": "POPIA",
  "Personal Data Protection Law No. 6698 (KVKK)": "KVKK",
  "Personal Data Protection Law (Saudi Arabia)": "PDPL (SA)",
  "Personal Data Protection Law (Oman, Royal Decree 6/2022)": "PDPL (OM)",
  "Personal Data Protection Law No. 13 of 2016": "PDPL (QA)",
  "Federal Personal Data Protection Law (PDPL)": "PDPL (UAE)",
  "DIFC Data Protection Law": "DIFC DPL",
  "ADGM Data Protection Regulations": "ADGM DPR",
  "Privacy Protection Law": "PPL (IL)",
  "Digital Personal Data Protection Act 2023 (DPDP)": "DPDP (IN)",
  "Personal Data Protection Act 2012 (PDPA)": "PDPA (SG)",
  "Personal Data Protection Act 2010 (PDPA)": "PDPA (MY)",
  "Personal Data Protection Act 2019 (PDPA)": "PDPA (TH)",
  "Personal Data Protection Act (PDPA)": "PDPA (TW)",
  "Personal Information Protection Law (PIPL)": "PIPL",
  "Personal Information Protection Act (PIPA)": "PIPA (KR)",
  "Act on the Protection of Personal Information (APPI)": "APPI",
  "Privacy Act 1988": "Privacy Act 1988 (AU)",
  "Australian Privacy Principles (APPs)": "APPs",
  "Notifiable Data Breaches Scheme": "NDB Scheme",
  "Privacy Act 2020": "Privacy Act 2020 (NZ)",
  "Personal Data (Privacy) Ordinance (PDPO)": "PDPO (HK)",
  "Data Privacy Act of 2012 (DPA)": "DPA 2012 (PH)",
  "Law on Personal Data Protection 2025 (Law 91/2025/QH15)": "PDP Law (VN)",
};

function shortRegulation(name: string): string {
  if (REGULATION_SHORT[name]) return REGULATION_SHORT[name];
  const abbr = /\(([A-Z0-9&/ -]{2,10})\)\s*$/.exec(name);
  if (abbr) return abbr[1];
  return name.length > 30 ? `${name.slice(0, 28)}…` : name;
}

function matchesStandard(t: PolicyTemplate, key: string): boolean {
  if (key === "playbooks") return Boolean(t.playbookId);
  const filterDef = STANDARD_FILTERS.find((f) => f.key === key);
  if (!filterDef) return true;
  return t.frameworkIds.some((fwId) => filterDef.frameworkIds.includes(fwId));
}

function formatControlId(id: string): string {
  // A Cyber Essentials requirement key ("fw_remote_protected") reads as its reference ("Cyber Essentials A4.10/11").
  const ce = controlRefWithStandard(id);
  if (ce?.startsWith("Cyber Essentials ")) return ce;
  if (id.startsWith("iso42001-"))    return `ISO 42001 ${id.slice(9)}`;
  if (id.startsWith("iso9001-"))     return `ISO 9001 ${id.slice(8)}`;
  if (id.startsWith("soc2-"))        return `SOC 2 ${id.slice(5)}`;
  if (id.startsWith("pci-req-"))     return `PCI DSS Req ${id.slice(8)}`;
  if (id.startsWith("pci-"))         return `PCI DSS ${id.slice(4)}`;
  if (id.startsWith("iasme-ca-"))    return `IASME CA Theme ${id.slice(id.lastIndexOf("-t") + 2)}`;
  if (id.startsWith("iso45001-"))    return `ISO 45001 ${id.slice(9)}`;
  if (id.startsWith("iso14001-"))    return `ISO 14001 ${id.slice(9)}`;
  if (id.startsWith("ncsc-caf-"))    return `NCSC CAF ${id.slice(9)}`;
  return `ISO 27001 ${id}`;
}

/**
 * Write policies — and, with `playbooks`, the sidebar's Playbooks area: the same cards, the same
 * generation, versions, staleness, connected-storage copies and adoption, narrowed to the playbook
 * templates the organisation's edition carries (docs/PLAYBOOKS.md).
 */
export function PolicyTemplatesPage({ playbooks = false }: { playbooks?: boolean } = {}) {
  const { enrolled, loading: frameworksLoading } = useFrameworks();
  // The playbooks this organisation's edition carries (GET /help/playbooks is edition-filtered).
  const editionPlaybooks = usePlaybooks();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState<PolicyTemplate[] | null>(null);
  // Every addendum of the organisation, grouped per policy on the cards (components/PolicyAddenda.tsx).
  const [addenda, setAddenda] = useState<PolicyAddendum[]>([]);
  const [orgRegulations, setOrgRegulations] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  // Deep links such as /templates?filter=data_protection preselect a filter chip.
  const [standardFilter, setStandardFilter] = useState(() => {
    const requested = searchParams.get("filter");
    return requested && STANDARD_FILTERS.some((f) => f.key === requested) ? requested : "all";
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [docStates, setDocStates] = useState<Record<string, DocState>>({});
  // Which generated policies this organisation has actually adopted. A draft is
  // not assessed by anything until a reviewed copy is on file, so the state
  // rides on the card rather than hiding in the compliance results.
  const [adoption, setAdoption] = useState<Record<string, PolicyAdoptionRow>>({});
  // Which policies answer several standards at once, and where two of them
  // answer the same requirement — advice to reference rather than repeat.
  const [overlap, setOverlap] = useState<PolicyOverlapReport | null>(null);
  // Figures two generated policies set differently, to be brought into line
  // before adoption.
  const [contradictions, setContradictions] = useState<PolicyContradiction[] | null>(null);
  const pollingIds = useRef<Set<string>>(new Set());
  // Why a regeneration would now write a document differently, per template.
  const [inputChanges, setInputChanges] = useState<Record<string, string[]>>({});
  // "Generate all applicable documents": the running or finished set job.
  const [setRun, setSetRun] = useState<SetRunView | null>(null);

  function loadAdoption() {
    api.get<{ policies: PolicyAdoptionRow[] }>("/templates/adoption")
      .then((d) => setAdoption(Object.fromEntries(d.policies.map((p) => [p.templateId, p]))))
      .catch(() => {});
  }

  function loadOverlap() {
    api.get<PolicyOverlapReport>("/templates/overlap").then(setOverlap).catch(() => {});
  }

  function loadInputChanges() {
    api.get<{ changes: Record<string, string[]> }>("/templates/input-changes")
      .then((d) => setInputChanges(d.changes ?? {}))
      .catch(() => {});
  }

  function loadContradictions() {
    api.get<{ contradictions: PolicyContradiction[] }>("/templates/contradictions")
      .then((d) => setContradictions(d.contradictions))
      .catch(() => {});
  }

  useEffect(() => {
    api.get<{ templates: PolicyTemplate[]; orgRegulations?: string[] }>("/templates").then((d) => {
      setTemplates(d.templates.map((t) => ({ ...t, regulations: t.regulations ?? [] })));
      setOrgRegulations(d.orgRegulations ?? []);
    });
    api.get<{ addenda: PolicyAddendum[] }>("/policy-addenda").then((d) => setAddenda(d.addenda)).catch(() => {});
    loadAdoption();
    loadOverlap();
    loadContradictions();
    loadInputChanges();
  }, []);

  // /templates?template=<id> (from a site's page) opens that policy's card.
  const requestedTemplate = searchParams.get("template");
  useEffect(() => {
    if (!templates || !requestedTemplate) return;
    const t = templates.find((x) => x.id === requestedTemplate);
    if (t && expanded !== t.id) void handleExpand(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, requestedTemplate]);

  // /templates?shortfall=<id> (My Actions, the calendar) opens the card of the policy the shortfall is against.
  const requestedShortfall = searchParams.get("shortfall");
  useEffect(() => {
    if (!templates || !requestedShortfall) return;
    api.get<{ shortfalls: Array<{ id: string; policyTemplateId: string }> }>("/policy-shortfalls")
      .then(({ shortfalls }) => {
        const s = shortfalls.find((x) => x.id === requestedShortfall);
        const t = s && templates.find((x) => x.id === s.policyTemplateId);
        if (t && expanded !== t.id) void handleExpand(t);
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, requestedShortfall]);

  // A consultant generates for a client only while the client allows it (My Organisation →
  // Consultant access). The API enforces this; the page only avoids offering a refused button.
  const [consultantDocGen, setConsultantDocGen] = useState<{ allowed: boolean } | null>(null);
  const isConsultant = user?.role === "consultant";
  useEffect(() => {
    if (!isConsultant) return;
    api.get<{ allowed: boolean }>("/organizations/consultant-docgen")
      .then(setConsultantDocGen)
      .catch(() => setConsultantDocGen({ allowed: false }));
  }, [isConsultant]);
  // Generating writes a document version, which the API allows only with documents:write (DOC-19).
  const canGenerate = !!user?.permissions?.includes("documents:write") && (user?.role === "client" || (isConsultant && !!consultantDocGen?.allowed));
  const canWriteAddenda = !!user?.permissions?.includes("documents:write");
  const canGenerateAddenda = canWriteAddenda && canGenerate;
  function addendumChanged(a: PolicyAddendum) {
    setAddenda((prev) => (prev.some((x) => x.id === a.id) ? prev.map((x) => (x.id === a.id ? a : x)) : [...prev, a]));
  }
  function addendumDeleted(id: string) {
    setAddenda((prev) => prev.filter((x) => x.id !== id));
  }

  // Restore any in-progress generations from localStorage on mount
  useEffect(() => {
    const map = getGeneratingMap();
    const now = Date.now();
    const valid: Record<string, GeneratingEntry> = {};
    for (const [id, entry] of Object.entries(map)) {
      if (now - entry.startedAt < POLICY_GENERATION_WAIT_MS) valid[id] = entry;
    }
    setGeneratingMap(valid);
    if (Object.keys(valid).length === 0) return;
    setDocStates((prev) => {
      const next = { ...prev };
      for (const [id, entry] of Object.entries(valid)) {
        if (prev[id]?.status !== "loaded") {
          next[id] = { status: "generating", generatingStartedAt: entry.startedAt };
        }
      }
      return next;
    });
    for (const [id, entry] of Object.entries(valid)) {
      if (!entry.jobId) continue;
      void followGeneration(id, pollDocumentJob<{ document: GeneratedDoc; content: string }>(entry.jobId, {
        ...trackJob(id),
        waitMs: POLICY_GENERATION_WAIT_MS - (now - entry.startedAt),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep pollingIds ref in sync with docStates
  useEffect(() => {
    for (const [id, ds] of Object.entries(docStates)) {
      if (ds.status === "generating") pollingIds.current.add(id);
      else pollingIds.current.delete(id);
    }
  }, [docStates]);

  // Single long-lived polling interval — checks all currently-generating templates
  useEffect(() => {
    const interval = setInterval(async () => {
      if (pollingIds.current.size === 0) return;
      for (const templateId of [...pollingIds.current]) {
        if (getGeneratingMap()[templateId]?.jobId) continue; // followed through its job
        try {
          const data = await api.get<{ document: GeneratedDoc | null; content?: string }>(`/templates/${templateId}/generated`);
          // Only a version written since the generation started: while a regeneration
          // runs, the previous version is still the latest one on file.
          const startedAt = getGeneratingMap()[templateId]?.startedAt ?? 0;
          if (data.document && data.content && new Date(data.document.generatedAt).getTime() >= startedAt - 5000) {
            setDocStates((prev) => ({ ...prev, [templateId]: { status: "loaded", document: data.document!, content: data.content } }));
            clearGenerating(templateId);
          }
        } catch { /* ignore transient errors — keep polling */ }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  function setDocState(templateId: string, patch: Partial<DocState>) {
    setDocStates((prev) => ({ ...prev, [templateId]: { ...((prev[templateId]) || { status: "idle" }), ...patch } }));
  }

  async function handleExpand(t: PolicyTemplate) {
    const next = expanded === t.id ? null : t.id;
    setExpanded(next);
    if (!next) return;
    const current = docStates[t.id]?.status;
    if (current === "loaded" || current === "generating") return;
    setDocState(t.id, { status: "checking" });
    try {
      const data = await api.get<{ document: GeneratedDoc | null; content?: string }>(`/templates/${t.id}/generated`);
      if (data.document && data.content) {
        setDocState(t.id, { status: "loaded", document: data.document, content: data.content });
      } else {
        setDocState(t.id, { status: "idle" });
      }
    } catch {
      setDocState(t.id, { status: "idle" });
    }
  }

  /** The worker's reports for one document's job, onto its card. */
  function trackJob(templateId: string) {
    return {
      onProgress: (p: AnyJobProgress) => { if (isDocumentProgress(p)) setDocState(templateId, { progress: p, queuedAt: undefined }); },
      onStatus: (st: JobStatus) => { if (st.status === "queued") setDocState(templateId, { queuedAt: st.queuedAt }); },
    };
  }

  /** Waits for a document job and settles its card: the new version, or why it failed. */
  async function followGeneration(templateId: string, job: Promise<{ document: GeneratedDoc; content: string }>) {
    try {
      const data = await job;
      clearGenerating(templateId);
      setDocState(templateId, { status: "loaded", document: data.document, content: data.content, progress: undefined, queuedAt: undefined });
      // A new policy version leaves its addenda flagged for review — pick up the new freshness.
      api.get<{ addenda: PolicyAddendum[] }>("/policy-addenda").then((d) => setAddenda(d.addenda)).catch(() => {});
      // …and leaves the policy itself a draft again until the new text is adopted.
      loadAdoption();
      loadOverlap();
      loadContradictions();
      loadInputChanges();
    } catch (e: unknown) {
      clearGenerating(templateId);
      const reason = e instanceof Error && e.message ? e.message : "The document could not be generated.";
      setDocState(templateId, {
        status: "error",
        error: `Generation failed — ${reason.replace(/\.?$/, ".")} Nothing was saved and any previous version is unchanged.`,
        progress: undefined,
        queuedAt: undefined,
      });
    }
  }

  async function generate(t: PolicyTemplate) {
    const startedAt = Date.now();
    markGenerating(t.id, { startedAt });
    setDocState(t.id, { status: "generating", generatingStartedAt: startedAt, error: undefined, progress: undefined, queuedAt: undefined });
    await followGeneration(t.id, generatePolicyDocument<GeneratedDoc>(t.id, {
      ...trackJob(t.id),
      onJobId: (jobId) => markGenerating(t.id, { startedAt, jobId }),
    }));
  }

  /** A rewritten section is a new version: show it and refresh everything that reads the documents. */
  function sectionRewritten(templateId: string, data: { document: GeneratedDoc; content: string }) {
    setDocState(templateId, { status: "loaded", document: data.document, content: data.content, error: undefined });
    api.get<{ addenda: PolicyAddendum[] }>("/policy-addenda").then((d) => setAddenda(d.addenda)).catch(() => {});
    loadAdoption();
    loadOverlap();
    loadContradictions();
    loadInputChanges();
  }

  // ── Generate all applicable documents ──
  // Refs, because a set job resumed on mount finishes long after the render that started it.
  const templateTitles = useRef<Record<string, string>>({});
  useEffect(() => {
    if (templates) templateTitles.current = Object.fromEntries(templates.map((t) => [t.id, t.title]));
  }, [templates]);
  const expandedRef = useRef<string | null>(null);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  function onSetProgress(p: AnyJobProgress) {
    if (isDocumentProgress(p)) return;
    setSetRun({ status: "running", done: p.done, total: p.total, currentTitle: p.current ? templateTitles.current[p.current] ?? null : null });
  }

  async function finishSet(promise: Promise<{ results: SetGenerationResult[] }>) {
    try {
      const { results } = await promise;
      setSetJob(null);
      const titled = (results ?? []).map((r) => ({ ...r, title: templateTitles.current[r.templateId] ?? r.templateId }));
      setSetRun({ status: "done", done: titled.length, total: titled.length, results: titled });
      // Forget what the page holds for the documents just written, and reload the open one.
      const written = new Set(titled.filter((r) => r.ok).map((r) => r.templateId));
      setDocStates((prev) => {
        const next = { ...prev };
        for (const id of written) if (next[id]?.status !== "generating") delete next[id];
        return next;
      });
      const open = expandedRef.current;
      if (open && written.has(open)) {
        api.get<{ document: GeneratedDoc | null; content?: string }>(`/templates/${open}/generated`)
          .then((data) => {
            if (data.document && data.content) setDocState(open, { status: "loaded", document: data.document, content: data.content });
          })
          .catch(() => {});
      }
      api.get<{ addenda: PolicyAddendum[] }>("/policy-addenda").then((d) => setAddenda(d.addenda)).catch(() => {});
      loadAdoption();
      loadOverlap();
      loadContradictions();
      loadInputChanges();
    } catch (e: unknown) {
      setSetJob(null);
      setSetRun({ status: "error", done: 0, total: 0, error: e instanceof Error ? e.message : "The documents could not be generated. Please try again." });
    }
  }

  // Pick up a set generation started before this page was (re)loaded.
  useEffect(() => {
    const job = getSetJob();
    if (!job) return;
    const elapsed = Date.now() - job.startedAt;
    if (elapsed > SET_GENERATION_WAIT_MS) { setSetJob(null); return; }
    setSetRun({ status: "running", done: 0, total: 0 });
    void finishSet(pollDocumentJob<{ results: SetGenerationResult[] }>(job.jobId, {
      onProgress: onSetProgress,
      waitMs: SET_GENERATION_WAIT_MS - elapsed,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function generateSet(ids: string[]) {
    if (ids.length === 0 || setRun?.status === "running") return;
    const n = ids.length;
    if (!window.confirm(
      `Generate ${n} ${n === 1 ? "document" : "documents"} now?\n\n` +
      "Each document is written by AI and uses AI credit. A document already generated gets a new version, which is a draft until you adopt it again.",
    )) return;
    setSetRun({ status: "running", done: 0, total: n });
    void finishSet(runDocumentJob<{ results: SetGenerationResult[] }>("/templates/generate-set", { templateIds: ids }, {
      onJobId: (jobId) => setSetJob({ jobId, startedAt: Date.now() }),
      onProgress: onSetProgress,
      waitMs: SET_GENERATION_WAIT_MS,
    }));
  }

  // The .docx is generated server-side (document-control table, brand colours, tables,
  // callouts) and served from Blob Storage with its filename set by the API.
  async function downloadDocx(docId: string) {
    const data = await api.get<{ url: string }>(`/templates/generated/${docId}/download-url`);
    const a = document.createElement("a");
    a.href = data.url;
    a.download = "";
    a.click();
  }

  if (!templates || frameworksLoading || (playbooks && !editionPlaybooks)) return <LoadingBlock />;

  // Playbooks apply to every organisation (good practice, not a standard's requirement), so the
  // Playbooks area offers every one its edition carries; Write policies offers those its standards call for.
  const editionPlaybookIds = new Set((editionPlaybooks ?? []).map((pb) => pb.id));
  // The organisation's own standards, in catalogue order — never a fixed list (REP-44).
  const enrolledNames = FRAMEWORK_ORDER.filter((id) => enrolled.has(id)).map((id) => FRAMEWORK_LABELS[id]).join(", ");
  const enrolledTemplates = playbooks
    ? templates.filter((t) => t.playbookId && editionPlaybookIds.has(t.playbookId))
    : templates.filter((t) => isTemplateEnrolled(t, enrolled, orgRegulations) && (!t.playbookId || editionPlaybookIds.has(t.playbookId)));
  const hasDataProtection = enrolledTemplates.some(isDataProtectionTemplate);
  const activeFilters = playbooks ? [] : STANDARD_FILTERS.filter((f) =>
    f.key === "data_protection" ? hasDataProtection
      : f.key === "playbooks" ? enrolledTemplates.some((t) => t.playbookId)
      : f.frameworkIds.some((id) => enrolled.has(id as never))
  );
  const nothingApplies = !playbooks && enrolled.size === 0 && !hasDataProtection;

  const searchLower = search.toLowerCase();
  let visible = enrolledTemplates;
  if (standardFilter !== "all") visible = visible.filter((t) => matchesStandard(t, standardFilter));
  if (search) {
    visible = visible.filter(
      (t) => t.title.toLowerCase().includes(searchLower)
        || t.controls.some((c) => c.id.toLowerCase().includes(searchLower))
        || t.ceControls.some((c) => c.ref.toLowerCase() === searchLower || c.name.toLowerCase().includes(searchLower))
        || t.regulations.some((r) => r.toLowerCase().includes(searchLower) || shortRegulation(r).toLowerCase().includes(searchLower))
    );
  }

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>{playbooks ? "Playbooks" : "Write policies"}</h1>
      {playbooks ? (
        <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
          Your own playbooks, generated like your policies: for every step of the published guidance, what your organisation does — who, with which records, by when — written from your records, laws and insurance, with the guidance quoted word for word beneath it. Decisions the guidance leaves to you show what the sources say and a recommendation for your organisation. The published guidance alone is in Help → Playbooks.
        </p>
      ) : (
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        AI-generated documents tailored to your organisation — policies tagged to your enrolled standards{enrolledNames ? ` (${enrolledNames})` : ""}{hasDataProtection ? ", playbooks, and the notices, procedures, registers and agreements required by the data protection laws that apply in your operating locations" : " and playbooks"}. Generated documents are saved to your account.
      </p>
      )}
      <p style={{ margin: "-12px 0 20px", fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
        What is generated here is a <strong style={{ color: theme.color.muted }}>draft</strong>. Compliance checks ignore it until you have reviewed it and your copy is in the Evidence Repository or in your connected storage — then it is assessed as your organisation's own commitment, and what you actually do is measured against it.
      </p>

      {isConsultant && consultantDocGen && !consultantDocGen.allowed && (
        <div style={{ fontSize: 12, color: theme.color.warningText, background: theme.color.warning + "22", border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "8px 12px", marginBottom: 16, lineHeight: 1.6 }}>
          This client has not allowed its consultant to generate documents. You can read what has been generated; an administrator of the client can switch generation on under My Organisation → Consultant access.
        </div>
      )}

      {/* Every document the page treats as applying to this organisation, written in one run. */}
      {canGenerate && !nothingApplies && enrolledTemplates.length > 0 && (
        <GenerateSetPanel
          count={Math.min(enrolledTemplates.length, SET_GENERATION_MAX)}
          run={setRun}
          onStart={() => generateSet(enrolledTemplates.slice(0, SET_GENERATION_MAX).map((t) => t.id))}
          onDismiss={() => setSetRun(null)}
        />
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          style={{ ...inputStyle, maxWidth: 280 }}
          placeholder="Search by title, control ID or regulation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {activeFilters.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[{ key: "all", label: "All" }, ...activeFilters].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStandardFilter(key)}
                style={{
                  padding: "7px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${standardFilter === key ? theme.color.accent : theme.color.borderStrong}`,
                  background: standardFilter === key ? theme.color.accentBg : theme.color.panel,
                  color: standardFilter === key ? theme.color.accentText : theme.color.subtle,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* One rule, one policy: where two documents answer the same requirement. */}
      {!nothingApplies && <PolicyOverlapPanel report={overlap} />}

      {/* One figure, one value: where two documents commit to different numbers. */}
      {!nothingApplies && <PolicyContradictionsPanel contradictions={contradictions} />}

      {nothingApplies ? (
        <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
          No compliance frameworks selected. Go to{" "}
          <PortalLink to="/my-services" style={{ color: theme.color.accentText }}>My Services</PortalLink>{" "}
          to choose the standards your organisation is working towards, or add your operating locations in{" "}
          <PortalLink to="/org-profile" style={{ color: theme.color.accentText }}>My Organisation</PortalLink>{" "}
          to unlock the data protection documents required where you operate.
        </div>
      ) : visible.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "48px 20px", color: theme.color.faint, fontSize: 13 }}>
          {enrolledTemplates.length === 0
            ? "No policy templates have been seeded yet. Ask your consultant to add templates."
            : "No templates match your search."}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
          {visible.map((t) => {
            const ds = docStates[t.id] || { status: "idle" };
            const isOpen = expanded === t.id;
            return (
              <div key={t.id} style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{t.title}</div>
                    {adoption[t.id] && <AdoptionChip state={adoption[t.id].state} />}
                    <InputsChangedChip reasons={inputChanges[t.id]} />
                    {overlap?.multiStandard.find((s) => s.templateId === t.id) && (
                      <StandardSpreadChip spread={overlap.multiStandard.find((s) => s.templateId === t.id)!} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
                    {t.category}{isDataProtectionTemplate(t) && t.kindLabel ? ` · ${t.kindLabel}` : ""}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{t.description}</div>
                  {t.playbookId && (
                    <ShellLink to={playbookPath(t.playbookId)} style={{ display: "inline-block", marginTop: 6, fontSize: 12, fontWeight: 600, color: theme.color.accentText, textDecoration: "none" }}>
                      The published guidance →
                    </ShellLink>
                  )}
                </div>
                {isDataProtectionTemplate(t) && (
                  // Only the laws that apply to this organisation are shown — and generated.
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {t.regulations.filter((r) => orgRegulations.includes(r)).map((r) => (
                      <span
                        key={r}
                        title={r}
                        style={{
                          fontSize: 10, fontWeight: 600, padding: "2px 7px",
                          background: theme.color.accentBg, color: theme.color.accentText,
                          borderRadius: 4, border: `1px solid ${theme.color.border}`,
                        }}
                      >
                        {shortRegulation(r)}
                      </span>
                    ))}
                  </div>
                )}
                {!isDataProtectionTemplate(t) && (t.controls.length > 0 || t.ceControls.length > 0) && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {chipControls(t.controls, enrolled).map((c) => (
                      <span
                        key={c.id}
                        title={c.name}
                        style={{
                          fontVariantNumeric: "tabular-nums", fontSize: 10, padding: "2px 6px",
                          background: theme.color.accentBg, color: theme.color.accentText,
                          borderRadius: 4, border: `1px solid ${theme.color.border}`,
                        }}
                      >
                        {formatControlId(c.id)}
                      </span>
                    ))}
                    {t.ceControls.map((c) => (
                      <span
                        key={`ce-${c.id}`}
                        title={`Cyber Essentials / Cyber Essentials Plus — ${c.ref} ${c.name}`}
                        style={{
                          fontVariantNumeric: "tabular-nums", fontSize: 10, padding: "2px 6px",
                          background: theme.color.accentBg, color: theme.color.accentText,
                          borderRadius: 4, border: `1px solid ${theme.color.border}`,
                        }}
                      >
                        {`CE ${c.ref} ${c.name}`}
                      </span>
                    ))}
                  </div>
                )}

                {addenda.some((a) => a.templateId === t.id) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {addenda.filter((a) => a.templateId === t.id).map((a) => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: theme.color.subtle }}>
                        <span style={{ fontWeight: 600, color: theme.color.muted }}>Addendum:</span>
                        <span>{a.title}</span>
                        <AddendumScopeChips addendum={a} />
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => handleExpand(t)}
                  style={{
                    padding: "7px 12px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    border: `1px solid ${theme.color.borderStrong}`,
                    background: theme.color.panelAlt,
                    color: theme.color.muted,
                    marginTop: "auto",
                  }}
                >
                  {isOpen ? "Hide" : ds.status === "loaded" ? "View document" : ds.status === "generating" ? "Generating…" : "View / Generate"}
                </button>

                {isOpen && (
                  <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 14, marginTop: 4 }}>

                    {/* Checking for existing doc */}
                    {ds.status === "checking" && (
                      <div style={{ fontSize: 12, color: theme.color.subtle }}>Checking for existing document…</div>
                    )}

                    {/* Idle: no existing doc, show generate button */}
                    {ds.status === "idle" && (
                      <div>
                        {!canGenerate && isConsultant ? (
                          <p style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12, lineHeight: 1.6 }}>
                            No document has been generated yet. Generating it for this client needs their consent: their administrator turns it on under My Organisation → Consultant access.
                          </p>
                        ) : <p style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12, lineHeight: 1.6 }}>
                          {isDataProtectionTemplate(t)
                            ? `No document has been generated yet. Click below to generate this ${(t.kindLabel || "document").toLowerCase()} using AI, written for ${t.regulations.filter((r) => orgRegulations.includes(r)).map(shortRegulation).join(", ")} and pre-populated from your organisation profile, records of processing and supplier register.`
                            : "No document has been generated yet. Click below to generate a complete policy document using AI, tailored to your organisation's profile."}
                        </p>}
                        {canGenerate && <button
                          onClick={() => generate(t)}
                          style={{
                            padding: "8px 18px", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer",
                            border: `1px solid ${theme.color.accent}`,
                            background: theme.color.accentBg,
                            color: theme.color.accentText,
                          }}
                        >
                          {isDataProtectionTemplate(t) ? `Generate ${t.kindLabel || "Document"}` : "Generate Policy Document"}
                        </button>}
                      </div>
                    )}

                    {/* Generating */}
                    {ds.status === "generating" && (
                      <GenerationProgressView
                        startedAt={ds.generatingStartedAt ?? Date.now()}
                        progress={ds.progress}
                        queuedAt={ds.queuedAt}
                      />
                    )}

                    {/* Error */}
                    {ds.status === "error" && (
                      <div>
                        <div style={{ fontSize: 12, color: theme.color.dangerText, background: theme.color.danger + "22", border: `1px solid ${theme.color.danger}`, borderRadius: 3, padding: "8px 12px", marginBottom: 10 }}>
                          {ds.error}
                        </div>
                        {canGenerate && <button
                          onClick={() => generate(t)}
                          style={{ padding: "7px 14px", borderRadius: 3, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText }}
                        >
                          Try again
                        </button>}
                      </div>
                    )}

                    {/* Loaded: show document */}
                    {ds.status === "loaded" && ds.document && ds.content && (
                      <div>
                        {/* What this policy owns, and what it should reference rather than restate. */}
                        <PolicyOverlapHints templateId={t.id} report={overlap} />
                        {/* Generated is not adopted: the check assesses the reviewed copy, not this draft. */}
                        <PolicyAdoptionPanel
                          templateId={t.id}
                          row={adoption[t.id]}
                          canWrite={canWriteAddenda}
                          onAdopted={(row) => setAdoption((prev) => ({ ...prev, [row.templateId]: row }))}
                        />
                        {ds.document.staleSince && (
                          <div style={{ fontSize: 11, color: theme.color.warningText, background: theme.color.warning + "22", border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "6px 10px", marginBottom: 10 }}>
                            {ds.document.staleReason === "template_updated"
                              ? "This template has been rewritten to current best practice since this version was generated. Regenerate to get the fuller document, then review and adopt it again."
                              : `Your organisation profile changed on ${new Date(ds.document.staleSince).toLocaleDateString("en-GB")}. Consider regenerating this document.`}
                          </div>
                        )}
                        <InputsChangedNote reasons={inputChanges[t.id]} />
                        <ProvenanceWarnings provenance={ds.document.provenance} />

                        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 11, color: theme.color.faint }}>
                            {/* Read in UTC, as the adoption note above writes it, so the two never name different days. */}
                            Generated {fmtDateOnly(ds.document.generatedAt)}
                          </span>
                          <button
                            onClick={() => downloadDocx(ds.document!.id)}
                            style={{ padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText }}
                          >
                            Download .{t.fileFormat ?? "docx"}
                          </button>
                          {canGenerate && <button
                            onClick={() => generate(t)}
                            style={{ padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.color.borderStrong}`, background: "transparent", color: theme.color.muted }}
                          >
                            Regenerate
                          </button>}
                        </div>

                        <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "16px 18px", maxHeight: 480, overflowY: "auto" }}>
                          <MarkdownDocument content={ds.content} size={12} />
                        </div>

                        <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 8 }}>
                          AI-generated — review before use in an audit or formal process.
                        </div>

                        <ProvenancePanel provenance={ds.document.provenance} />

                        {canGenerate && (
                          <SectionRewriteForm<GeneratedDoc>
                            key={ds.document.id}
                            templateId={t.id}
                            content={ds.content}
                            onRewritten={(data) => sectionRewritten(t.id, data)}
                          />
                        )}
                      </div>
                    )}

                    {/* Lessons learned: reviews and findings that showed this policy fell short (components/PolicyShortfalls.tsx). */}
                    <PolicyShortfallList templateId={t.id} canClose={canWriteAddenda} highlight={requestedShortfall} />

                    {/* Standing instructions, given to every generation of this document. */}
                    {ds.status !== "checking" && (canGenerate || canWriteAddenda) && (
                      <TemplateInstructions templateId={t.id} canWrite={canWriteAddenda} />
                    )}

                    {/* Local supplements for particular offices / jurisdictions */}
                    {ds.status !== "checking" && (
                      <PolicyAddendaSection
                        templateId={t.id}
                        templateTitle={t.title}
                        items={addenda.filter((a) => a.templateId === t.id)}
                        policyGenerated={ds.status === "loaded"}
                        canWrite={canWriteAddenda}
                        canGenerate={canGenerateAddenda}
                        onChanged={addendumChanged}
                        onDeleted={addendumDeleted}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
